import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, structureReportPath, type GrateStyle, type StructureDetail, type StructureType, type StructureWrite } from '../lib/api';
import {
  StructureNotesPhotosFields,
  uploadStructurePhotos,
} from '../lib/StructureNotesPhotosFields';
import { clearUnsavedChanges } from '../lib/UnsavedChanges';
import { HoverTooltip } from '../lib/HoverTooltip';

const NYLOPLAST_BASIN_SIZES = [12, 15, 18, 24, 30] as const;

/**
 * Step 3 (Nyloplast). Bootstrap 5 + Hyper theme markup mirroring
 * `resources/jsx/components/forms/nyloplastforms.jsx`.
 */
export function NyloplastDimensionsForm() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isPermanent = params.get('temp') !== 'true';
  const oilFiltration = isPermanent && params.get('oil') === 'true';

  const editIdStr = params.get('id');
  const editId = editIdStr?.trim() || null;
  const isEdit = !!editId;

  const [nickname, setNickname] = useState('');
  const [basinSize, setBasinSize] = useState<number | null>(null);
  const [grateStyleId, setGrateStyleId] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [showExtras, setShowExtras] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const typesQuery = useQuery({
    queryKey: ['structure-types'],
    queryFn: () => api.get<StructureType[]>('/api/v1/structure-types'),
  });

  // Edit mode: when the report's "Edit Structure Dimensions" link supplies an
  // `id`, load the existing structure and pre-fill the form (legacy parity with
  // `edit_structure` step 4), then PUT on submit instead of creating a new one.
  const existingQuery = useQuery({
    queryKey: ['structure', editId],
    queryFn: () => api.get<StructureDetail>(`/api/v1/structures/${editId}`),
    enabled: isEdit,
  });

  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!isEdit || !existingQuery.data || hydratedRef.current) return;
    hydratedRef.current = true;
    const s = existingQuery.data;
    setNickname(s.nickname ?? '');
    setBasinSize(s.basinSize ?? null);
    setGrateStyleId(s.grateStyleId ?? null);
    setNotes(s.structureNotes ?? '');
  }, [isEdit, existingQuery.data]);

  const nyloplastType = useMemo(
    () =>
      typesQuery.data?.find(
        (t) =>
          t.isNyloplast &&
          t.family !== 'INVALID STRUCTURE' &&
          t.name !== 'INVALID STRUCTURE',
      ) ?? null,
    [typesQuery.data],
  );

  const grateStylesQuery = useQuery({
    queryKey: ['grate-styles', nyloplastType?.id],
    queryFn: () => api.get<GrateStyle[]>(`/api/v1/grate-styles?structureTypeId=${nyloplastType!.id}`),
    enabled: !!nyloplastType,
  });

  // Mirror legacy nyloplastforms.jsx: only show grate styles whose basin_size
  // matches the currently selected basin diameter. Without this filter the
  // dropdown either appears empty (showing only the placeholder) or lists
  // every style across every basin size. Preserve legacy ordering (by id /
  // insertion order) since the API returns them sorted by name.
  const filteredGrateStyles = useMemo(() => {
    if (basinSize === null) return [];
    return (grateStylesQuery.data ?? [])
      .filter((g) => g.basinSize === basinSize)
      .sort((a, b) => a.id - b.id);
  }, [grateStylesQuery.data, basinSize]);

  const canSubmit = !!nyloplastType && basinSize !== null && !submitting;

  const svgRef = useRef<HTMLObjectElement>(null);
  const svgMobileRef = useRef<HTMLObjectElement>(null);

  // Legacy parity (nyloplastforms.jsx): inject the selected basin size into the
  // embedded SVG's `basin_size_output` text node. This requires embedding the
  // SVG via `<object>` (so its inner DOM is reachable), not an `<img>`.
  const writeOutputs = useCallback(() => {
    const text = basinSize !== null ? `${basinSize} inches` : '';
    for (const obj of [svgRef.current, svgMobileRef.current]) {
      const doc = obj?.contentDocument;
      if (!doc) continue;
      for (const id of ['basin_size_output', 'basin_size_output_2']) {
        const el = doc.getElementById(id);
        if (el) el.textContent = text;
      }
    }
  }, [basinSize]);

  useEffect(() => {
    writeOutputs();
  }, [writeOutputs]);

  const submit = async () => {
    if (!canSubmit || !nyloplastType) return;
    setSubmitting(true);
    setError(null);
    try {
      const body: StructureWrite = {
        nickname: nickname.trim() || null,
        permanent: isPermanent,
        oilFiltration,
        structureTypeId: nyloplastType.id,
        grateStyleId,
        basinSize,
        extendedBottomHeavyDuty: false,
        structureNotes: notes.trim() || null,
        // Legacy parity: freshly generated structures are drafts (isdraft
        // defaults true); editing never changes an existing draft state.
        isDraft: isEdit ? (existingQuery.data?.isDraft ?? true) : true,
      };
      if (isEdit && editId) {
        const saved = await api.put<StructureDetail>(`/api/v1/structures/${editId}`, body);
        queryClient.setQueryData(['structure', editId], saved);
        await uploadStructurePhotos(saved.id, photoFiles);
        clearUnsavedChanges();
        navigate(structureReportPath(saved.hashId, true));
      } else {
        const created = await api.post<StructureDetail>('/api/v1/structures', body);
        await uploadStructurePhotos(created.id, photoFiles);
        clearUnsavedChanges();
        navigate(structureReportPath(created.hashId, true));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save structure');
      setSubmitting(false);
    }
  };

  return (
    <div className="container-fluid" data-unsaved-guard>
      <div className="row mt-lg-3 mt-1">
        <div className="col col-xl-12 col-lg-12">
          <div className="container-fluid">
            <div className="row justify-content-center">
              <div className="col-12 text-center">
                <h1>Nyloplast Castings Configuration</h1>
                <p>Enter your dimensions to the nearest tenth of an inch.</p>
              </div>
            </div>

            <div className="row justify-content-center">
              <div className="col-lg-6 p-0 pe-lg-5 py-3 py-md-0 text-center">
                <object
                  ref={svgRef}
                  type="image/svg+xml"
                  className="d-none d-lg-block mx-auto"
                  data="/images/DRAWINGS/NylosPlast.svg"
                  aria-label="Nyloplast measurement diagram"
                  onLoad={writeOutputs}
                />
                <object
                  ref={svgMobileRef}
                  type="image/svg+xml"
                  className="d-block d-lg-none mx-auto"
                  data="/images/DRAWINGS/NylosPlast-Mobile.svg"
                  aria-label="Nyloplast measurement diagram"
                  onLoad={writeOutputs}
                />
              </div>

              <div className="col-12 col-lg-6 border-lg-start ps-lg-5 text-start">
                {!showExtras && (<>
                <div className="mb-2">
                  <label className="form-label fs-5" htmlFor="StructureNickname">
                    Structure Name (Optional)
                    <HoverTooltip
                      text="Give your structure a name to find it easier in the future. Locations, structure purpose, or dates all make good names."
                      maxWidth={260}
                    >
                      <img
                        className="ps-2"
                        src="/images/ICON/Icon_Info_Green_Filled_Small.png"
                        alt=""
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </HoverTooltip>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="StructureNickname"
                    id="StructureNickname"
                    placeholder="Structure Name"
                    maxLength={120}
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                  />
                </div>

                <div className="mb-2">
                  <label className="form-label fs-5">
                    Basin Size (Diameter Nyloplast Casting)
                  </label>
                  <div className="row">
                    {NYLOPLAST_BASIN_SIZES.map((size) => (
                      <div key={size} className="col d-grid pt-1 gap-2">
                        <input
                          type="radio"
                          className="btn-check"
                          name="basin_size"
                          id={`basin_size_${size}`}
                          autoComplete="off"
                          checked={basinSize === size}
                          onChange={() => {
                            setBasinSize(size);
                            // Reset grate style when basin changes; selections
                            // are scoped to a specific basin diameter.
                            setGrateStyleId(null);
                          }}
                        />
                        <label className="btn p-1 btn-outline-primary" htmlFor={`basin_size_${size}`}>
                          {size}&Prime;
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {basinSize !== null && (
                  <div className="mb-2">
                    <label className="form-label fs-5" htmlFor="grate_style">Grate Style</label>
                    <select
                      id="grate_style"
                      className="form-select form-select-sm"
                      value={grateStyleId ?? ''}
                      onChange={(e) => setGrateStyleId(e.target.value === '' ? null : Number(e.target.value))}
                    >
                      <option value="" disabled hidden>Grate Style</option>
                      {filteredGrateStyles.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                </>)}

                {error && (
                  <div className="alert alert-warning fade show" role="alert">
                    {error}
                  </div>
                )}

                {showExtras && (
                  <StructureNotesPhotosFields
                    notes={notes}
                    onNotesChange={setNotes}
                    files={photoFiles}
                    onFilesChange={setPhotoFiles}
                    disabled={submitting}
                  />
                )}

                <div className="mb-2 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      if (!showExtras) {
                        if (!canSubmit) return;
                        setShowExtras(true);
                      } else {
                        submit();
                      }
                    }}
                    disabled={!canSubmit}
                    className="btn btn-sm mt-2 py-1 btn-primary"
                  >
                    {submitting ? 'Saving…' : showExtras ? 'Submit' : 'Continue'}
                  </button>
                </div>
              </div>

              <div className="col-12 text-center">
                <Link
                  to="/newstructure"
                  className="btn btn-sm mt-2 py-1 btn-outline-secondary"
                >
                  Restart Configuration
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
